import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, Users, DollarSign, Target, ChevronDown, Zap } from 'lucide-react';
import { Client, countsTowardMRR } from '@/types/crm';

interface ClientPipelineSummaryProps {
  clients: Client[];
  loading?: boolean;
}

const COLLAPSED_KEY = 'crm-summary-collapsed';
const SUMMARY_PANEL_ID = 'pipeline-summary-panel';

export function ClientPipelineSummary({ clients, loading }: ClientPipelineSummaryProps) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate summary metrics
  const activeClients = clients.filter(c => c.lifecycle_stage === 'active');
  const committedClients = clients.filter(c => c.lifecycle_stage === 'committed');
  const pipelineClients = clients.filter(c => ['opportunity', 'interest', 'deck_sent', 'closing'].includes(c.lifecycle_stage));

  const clientValue = (c: Client) => c.confirmed_monthly_revenue || c.estimated_monthly_value || 0;

  // One-off deals are single payments, so they are held out of every MRR figure
  // below and reported on their own line instead.
  const recurringOnly = (list: Client[]) => list.filter(c => countsTowardMRR(c.revenue_type));

  // Active revenue (100% guaranteed)
  const activeRevenue = recurringOnly(activeClients).reduce((sum, c) => sum + clientValue(c), 0);

  // Committed revenue (raw total)
  const committedRevenue = recurringOnly(committedClients).reduce((sum, c) => sum + clientValue(c), 0);

  // Pipeline revenue (raw total)
  const pipelineTotal = recurringOnly(pipelineClients).reduce((sum, c) =>
    sum + (c.estimated_monthly_value || 0), 0
  );

  // One-off revenue across every stage — excluded from the MRR figures above
  const oneOffClients = [...activeClients, ...committedClients, ...pipelineClients]
    .filter(c => !countsTowardMRR(c.revenue_type));
  const oneOffTotal = oneOffClients.reduce((sum, c) => sum + clientValue(c), 0);

  // Total pipeline value (raw sum of all stages, recurring only)
  const totalPipelineValue = activeRevenue + committedRevenue + pipelineTotal;

  const toggle = (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-expanded={!collapsed}
      aria-controls={SUMMARY_PANEL_ID}
      className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-2 h-11 sm:h-8 sm:py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
    >
      <span>Pipeline Summary</span>
      <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
    </button>
  );

  if (loading) {
    return (
      <div>
        {toggle}
        {!collapsed && (
          <div id={SUMMARY_PANEL_ID} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-8 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {toggle}
      {!collapsed && (
        <div id={SUMMARY_PANEL_ID} className={`grid grid-cols-2 gap-4 ${oneOffClients.length > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
          {/* Total Pipeline — neutral muted */}
          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span>Total Pipeline</span>
              </div>
              <div className="text-2xl font-bold font-serif text-foreground">
                {formatCurrency(totalPipelineValue)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                All stages combined
              </div>
            </CardContent>
          </Card>

          {/* Pipeline — gold accent */}
          <Card className="bg-accent/10 border-accent/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span>Pipeline</span>
              </div>
              <div className="text-2xl font-bold font-serif text-foreground">
                +{formatCurrency(pipelineTotal)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {pipelineClients.length} prospect{pipelineClients.length !== 1 ? 's' : ''}
              </div>
            </CardContent>
          </Card>

          {/* Signed Revenue — green primary */}
          <Card className="bg-primary/10 border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Target className="h-4 w-4" />
                <span>Signed</span>
              </div>
              <div className="text-2xl font-bold font-serif text-foreground">
                +{formatCurrency(committedRevenue)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {committedClients.length} starting soon
              </div>
            </CardContent>
          </Card>

          {/* Active Revenue — bright gold accent */}
          <Card className="bg-accent/20 border-accent/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                <span>Active MRR</span>
              </div>
              <div className="text-2xl font-bold font-serif text-foreground">
                {formatCurrency(activeRevenue)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {activeClients.length} active client{activeClients.length !== 1 ? 's' : ''}
              </div>
            </CardContent>
          </Card>

          {/* One-Off — reported separately, never part of MRR */}
          {oneOffClients.length > 0 && (
            <Card className="bg-muted/50 border-dashed border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Zap className="h-4 w-4" />
                  <span>One-Off</span>
                </div>
                <div className="text-2xl font-bold font-serif text-foreground">
                  {formatCurrency(oneOffTotal)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {oneOffClients.length} deal{oneOffClients.length !== 1 ? 's' : ''} — not in MRR
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
